import type { BandRole, Profile } from './core/types';
import { NM } from './core/constants';
import { INDICES } from './render/indices';
import type { State } from './state/store';

export interface Sample {
  name: string; url: string; group?: string; description?: string;
  bands?: string[]; domain?: { scale?: number; offset?: number; label?: string };
  mode?: string; index?: string; ramp?: string; min?: number; max?: number;
}

/** Used when samples.json is missing — the dropdown never simply vanishes. */
export const BUILTIN_SAMPLES: Sample[] = [];

/** Keep only usable entries and resolve relative URLs against the list file. */
export function parseSamples(json: unknown, base: string): Sample[] {
  const list = (Array.isArray(json) ? json : (json as { samples?: unknown[] })?.samples) ?? [];
  return (list as Sample[])
    .filter((s) => s && s.url && s.name)
    .map((s) => {
      let url = s.url;
      try { url = new URL(s.url, base).href; } catch { /* keep as written */ }
      return { ...s, url };
    });
}

/**
 * A sample entry can supply what the file itself cannot: the band order for an
 * unnamed raster, a scale the file never declared, and the view worth opening on.
 * Applied after probing, so it overrides what was inferred.
 *
 * Mutates the profile's band roles in place and returns the state patch, mirroring how
 * a shared link is restored.
 */
export function applySample(
  sample: Sample, profile: Profile,
): Partial<State> {
  const patch: Partial<State> = {};
  if (Array.isArray(sample.bands)) {
    sample.bands.forEach((role, i) => {
      const b = profile.bands[i];
      if (!role || !b) return;                     // null leaves a band alone
      b.role = role as BandRole;
      b.wavelengthNm = NM[role as BandRole] ?? null;
      b.source = 'metadata';                       // declared, not guessed
      b.why = 'declared in the sample list';
    });
  }
  if (sample.domain && (sample.domain.scale != null || sample.domain.offset != null)) {
    profile.domain.candidates.unshift({
      id: 'sample', label: sample.domain.label ?? 'From the sample list',
      scale: sample.domain.scale ?? 1, offset: sample.domain.offset ?? 0,
      confidence: 'high', formula: `value × ${sample.domain.scale ?? 1}`,
      note: 'Scale and offset supplied by the sample list rather than inferred.',
    });
    profile.domain.selected = 0;
    profile.domain.ambiguous = false;
  }
  if (sample.mode) patch.mode = sample.mode as State['mode'];
  if (sample.index) patch.indexId = sample.index;
  if (sample.ramp) patch.rampId = sample.ramp;
  // An explicit range wins; otherwise fall back to the index's own default.
  const ix = sample.index ? INDICES.find((i) => i.id === sample.index) : undefined;
  if (ix) {
    if (!sample.ramp) patch.rampId = ix.ramp;
    if (sample.min == null && sample.max == null) {
      patch.lo = ix.range[0];
      patch.hi = ix.range[1];
    }
  }
  if (typeof sample.min === 'number') patch.lo = sample.min;
  if (typeof sample.max === 'number') patch.hi = sample.max;
  return patch;
}

export async function loadSamples(src = 'samples.json'): Promise<Sample[]> {
  try {
    const r = await fetch(src, { cache: 'no-cache' });
    if (!r.ok) throw new Error(String(r.status));
    const parsed = parseSamples(await r.json(), new URL(src, location.href).href);
    return parsed.length ? parsed : BUILTIN_SAMPLES;
  } catch { return BUILTIN_SAMPLES; }
}